/**
 * Admin content-jobs queries — Sunday Engine observability + ops
 * (list/summary/reset/delete) plus the health-check, analytics-digest,
 * daily-digest history, and scheduled-cleanup helpers that live on the
 * same AdminAIOps / AdminSystemHealth surfaces.
 *
 * Grouped together because they all target the Sunday Engine ops layer —
 * one page's worth of infra is one file.
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeJson } from '../_edge'
import {
  ADMIN_CONTENT_JOBS,
  HEALTH_CHECK,
  ANALYTICS_DIGEST,
  SCHEDULED_CLEANUP,
} from '../_endpoints'
import { ADMIN_DIGEST_DAYS } from '../_constants'

/** Run the health-check edge function (admin-only). */
export async function fetchHealthCheck() {
  return invokeEdgeJson(HEALTH_CHECK, {}, { errorMessage: 'Health check failed' })
}

/** Run the analytics-digest edge function (admin-only). */
export async function fetchAnalyticsDigest(date) {
  return invokeEdgeJson(ANALYTICS_DIGEST, date ? { date } : {}, {
    errorMessage: 'Analytics digest failed',
  })
}

/** Fetch historical daily digests from the daily_digest table. */
export async function fetchDailyDigestHistory(days = ADMIN_DIGEST_DAYS) {
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)
  const { data, error } = await supabase
    .from('daily_digest')
    .select('digest_date, metrics, created_at')
    .gte('digest_date', startDate.toISOString().split('T')[0])
    .order('digest_date', { ascending: false })
  if (error) throw error
  return data || []
}

// ── Sunday Engine: content_jobs durability queries ────────────
//
// All four admin actions route through the admin-content-jobs edge
// function. The table has RLS enabled with no policies, so direct
// client-side reads/writes silently fail — Codex audit fix #3
// (2026-04-20) moved them server-side with admin-JWT validation.

/**
 * Fetch content_jobs rows for the observability dashboard.
 * Returns DLQ + failed + running always; succeeded capped to last 48 hours
 * to keep the table focused on actionable state, not history.
 */
export async function fetchContentJobs() {
  const json = await invokeEdgeJson(
    ADMIN_CONTENT_JOBS,
    { action: 'list' },
    { errorMessage: 'admin-content-jobs list failed' }
  )
  return json.rows || []
}

/** Count of jobs in each status bucket — powers the dashboard summary cards. */
export async function fetchContentJobsSummary() {
  return invokeEdgeJson(
    ADMIN_CONTENT_JOBS,
    { action: 'summary' },
    { errorMessage: 'admin-content-jobs summary failed' }
  )
}

/** Reset a DLQ or failed job back to 'failed' so the retry-failed-jobs
 *  sweeper picks it up on its next tick. Preserves attempts (audit trail),
 *  bumps max_attempts by 3, increments manual_resets, clears lease_token.
 *  Admin-only escalation path per `_runbooks/dlq-recovery.md`. */
export async function resetContentJob(idempotencyKey) {
  return invokeEdgeJson(
    ADMIN_CONTENT_JOBS,
    { action: 'reset', idempotency_key: idempotencyKey },
    { errorMessage: 'admin-content-jobs reset failed' }
  )
}

/** Delete a content_jobs row — used when the underlying draft is killed
 *  and the job metadata should go with it. */
export async function deleteContentJob(idempotencyKey) {
  return invokeEdgeJson(
    ADMIN_CONTENT_JOBS,
    { action: 'delete', idempotency_key: idempotencyKey },
    { errorMessage: 'admin-content-jobs delete failed' }
  )
}

/** Run the scheduled-cleanup edge function (admin-only). */
export async function runScheduledCleanup(mode = 'dry_run') {
  return invokeEdgeJson(SCHEDULED_CLEANUP, { mode }, { errorMessage: 'Cleanup failed' })
}
